<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { maxLength, required } from '@regle/rules';
import useFoldersService from '@/composables/apiServices/useFoldersService';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import type { IEditFolderProps } from '@/types/components/settings/folders/EditFolder';
import type { ISettingsFolderMapped } from '@/types/components/settings/folders/SettingsFolderList';
import { getApiErrorByKey } from '@/helpers/api';
import type { ISettingsFolderRequest } from '@/types/services/folder.service';
import { FolderEditActionType } from '@/types/components/settings/folders/Consts';
import { isNotFolderItemChild } from '@/helpers/foldersUtils';
import { useBeforeLeave } from '@/composables/useBeforeLeave';
import BeforeLeavingModal from '@/components/before-leaving/BeforeLeavingModal.vue';
import { useRegleConfig } from '@/composables/useRegle';

const { t } = useI18n();
const { mobile } = useDisplay();

const { getFlatFolders, createFolder, updateFolder } = useFoldersService();
const { data: flatFolders } = getFlatFolders();

const {
  mutateAsync: creatingMutate,
  isPending: createIsPending,
  error: createError
} = createFolder();
const {
  mutateAsync: updatingMutate,
  isPending: updateIsPending,
  error: updateError
} = updateFolder();

const props = defineProps<IEditFolderProps>();

const panel = ref<[string]>(['']);
const isRetentionEnabled = ref(false);

const retentionDeletionList = computed(() => {
  return [
    {
      title: t('settings.folders.form.timePeriod.30'),
      value: 30
    },
    {
      title: t('settings.folders.form.timePeriod.60'),
      value: 60
    },
    {
      title: t('settings.folders.form.timePeriod.90'),
      value: 90
    },
    {
      title: t('settings.folders.form.timePeriod.180'),
      value: 180
    }
  ];
});

const currentErrorMessage = computed(() => {
  if (props.actionType === 'new') {
    return createError.value;
  } else {
    return updateError.value;
  }
});

const parseDefaultRetentionValue = () => {
  const retention = props.item?.retention ?? 30 * 24 * 60 * 60 * 1000;

  return retention / 24 / 60 / 60 / 1000;
};

const getPropItemParentPath = () => {
  const path = props.item?.path ?? '';
  const lastSlashIndex = path.lastIndexOf('/');
  return lastSlashIndex > 0 ? path.substring(0, lastSlashIndex) : null;
};

const getInitialValues = (): ISettingsFolderRequest => {
  return {
    name: props.item?.name ?? '',
    parent: getPropItemParentPath(),
    retention: parseDefaultRetentionValue(),
    hidden: props.item?.hidden ?? false
  };
};

const folderNameApiError = computed(() => getApiErrorByKey('name', currentErrorMessage));

const useRegle = useRegleConfig();
const { r$ } = useRegle(getInitialValues(), {
  name: props.isSystemFolder
    ? { maxLength: maxLength(512) }
    : { required, maxLength: maxLength(512) },
  parent: {},
  retention: {},
  hidden: {}
});

const handleFolderSubmit = async () => {
  const { valid, data } = await r$.$validate();
  if (!valid) return;

  const normalizedData: ISettingsFolderRequest = {
    name: data.name,
    parent: data.parent ?? null,
    retention: data.retention ?? null,
    hidden: data.hidden ?? false
  };

  if (props.actionType === FolderEditActionType.EDIT) {
    await updatingMutate({
      id: props.item?.id,
      data: {
        ...normalizedData,
        retention: isRetentionEnabled.value ? normalizedData.retention : null
      }
    });
  } else {
    await creatingMutate({
      ...normalizedData,
      retention: isRetentionEnabled.value ? normalizedData.retention : null
    });
    resetValues();
  }

  dialogOpener.value = false;
};

const parentFolderList = computed<ISettingsFolderMapped[]>(() => {
  return (flatFolders.value || [])
    .filter((folder) => isNotFolderItemChild(folder.path, props.item?.path))
    .map((folder) => ({
      name: folder.path,
      parent: folder.path
    }));
});

const resetValues = () => {
  r$.$value = getInitialValues() as ISettingsFolderRequest;
  r$.$reset();
};

const metaDirty = computed(() => r$.$anyDirty);
const { dialogLeave, dialogPopupRef, dialogOpener, dialogClose } = useBeforeLeave(metaDirty);

const handleFolderEdit = () => {
  if (props.isSystemFolder) {
    panel.value = ['advancedSettings'];
  }
  isRetentionEnabled.value = props.item?.retention ? true : false;
};

watch(dialogOpener, (value) => {
  if (value) {
    resetValues();
  }
});
</script>

<template>
  <VDialog v-model="dialogOpener" max-width="543" :persistent="metaDirty" class="modal-edit">
    <template #activator="{ props: dialogActivatorProps }">
      <!--  Settings update folder button  -->
      <v-tooltip
        v-if="isSystemFolder || isCustomFolder"
        :disabled="mobile"
        :text="t('common.edit')"
      >
        <template #activator="{ props: tooltipActivatorProps }">
          <CloseBtn
            :data-test-id="`settings-folders-list-item-edit-${props.item?.id}`"
            :size="props.icon ? 40 : ''"
            color="on-surface-variant"
            v-bind="{ ...dialogActivatorProps, ...tooltipActivatorProps }"
            @click.prevent="handleFolderEdit"
            sentry-unmask
          >
            <VIcon :class="{ 'mr-2': mobile }" size="24">mdi-pencil-outline</VIcon>
            <span v-if="mobile" class="modal-edit__activator text-body-lg">{{
              t('settings.folders.actions.mobileEdit')
            }}</span>
          </CloseBtn>
        </template>
      </v-tooltip>
      <!--  Sidebar add folder button  -->
      <TextBtn
        v-else-if="!props.settings && !isSystemFolder && !isCustomFolder"
        data-test-id="sidebar-edit-folder"
        v-bind="dialogActivatorProps"
        sentry-unmask
      >
        <VIcon class="mr-3" color="primary" size="24">mdi-plus</VIcon>
        <span class="modal-edit__activator">{{ t('sidebar.folders.addFolder') }}</span>
      </TextBtn>
      <!--  Settings add folder button  -->
      <FilledBtn
        v-else-if="props.settings && !isSystemFolder && !isCustomFolder"
        :class="{ 'w-100': mobile }"
        data-test-id="settings-edit-folder"
        v-bind="dialogActivatorProps"
        sentry-unmask
      >
        <template #prepend>
          <VIcon color="on-primary">mdi-plus</VIcon>
        </template>
        <span class="modal-edit__activator text-body-lg">{{ t('sidebar.folders.addFolder') }}</span>
      </FilledBtn>
    </template>

    <v-card>
      <v-card-title :class="{ 'px-0 pt-0': mobile }" sentry-unmask>
        {{
          props.item?.id
            ? t('settings.folders.form.editFolder')
            : t('settings.folders.form.newFolder')
        }}
      </v-card-title>
      <CloseBtn
        :data-test-id="`${props.actionType}-folder-close`"
        class="v-card__close-btn"
        color="on-surface"
        icon
        @click="dialogClose()"
        sentry-unmask
      >
        <v-icon>mdi-close</v-icon>
      </CloseBtn>

      <form :data-test-id="`${props.actionType}-folder`" @submit.prevent="handleFolderSubmit">
        <v-card-text
          v-if="!props.isSystemFolder"
          :class="{ 'px-4': !mobile, 'px-0': mobile }"
          class="pt-2"
        >
          <VLabel
            :class="{ 'pb-1': mobile, 'pb-2': !mobile }"
            class="cursor-pointer text-title-md on-surface"
            for="folder-name"
            sentry-unmask
          >
            {{ t('settings.folders.form.name') }}
          </VLabel>
          <VTextField
            id="folder-name"
            v-model="r$.$value.name"
            :error="r$.name.$error"
            :error-messages="folderNameApiError ?? r$.name.$errors"
            :placeholder="t('settings.folders.form.newFolder')"
            bg-color="false"
            clearable
            data-test-id="settings-folder-add-new-folder-name"
            name="name"
            variant="outlined"
          />

          <VLabel
            :class="{ 'pb-1': mobile, 'pb-2': !mobile }"
            class="cursor-pointer text-title-md on-surface"
            for="folder-parent"
            sentry-unmask
          >
            {{ t('settings.folders.form.parent') }}
          </VLabel>
          <VSelect
            id="folder-parent"
            v-model="r$.$value.parent"
            :items="parentFolderList"
            :placeholder="t('settings.folders.form.parent')"
            bg-color="false"
            clearable
            data-test-id="settings-select-folder-parent"
            item-title="name"
            item-value="parent"
            name="parent"
            variant="outlined"
            :list-props="{ density: 'compact', maxWidth: '508' }"
          />
        </v-card-text>

        <VExpansionPanels v-model="panel" :class="{ 'mb-6': !props.isSystemFolder }" flat>
          <VExpansionPanel
            :class="{
              'accordion-title': !props.isSystemFolder,
              'modal-edit__hidden': props.isSystemFolder
            }"
            :hide-actions="props.isSystemFolder"
            value="advancedSettings"
          >
            <VExpansionPanelTitle sentry-unmask>
              {{ t('settings.folders.info.advanced') }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <div :class="{ 'pb-3': mobile }" class="d-flex align-center justify-space-between">
                <VLabel
                  class="cursor-pointer text-title-md"
                  for="automatic-deletion-switch"
                  sentry-unmask
                >
                  {{ t('settings.folders.updateFolder.automatic') }}
                </VLabel>

                <v-tooltip
                  :text="
                    isRetentionEnabled
                      ? t('settings.folders.updateFolder.disableAutomatic')
                      : t('settings.folders.updateFolder.enableAutomatic')
                  "
                >
                  <template #activator="{ props: tooltipActivatorProps }">
                    <v-switch
                      sentry-unmask
                      id="automatic-deletion-switch"
                      v-model="isRetentionEnabled"
                      data-test-id="settings-folders-list-item-edit-form"
                      hide-details
                      v-bind="tooltipActivatorProps"
                    />
                  </template>
                </v-tooltip>
              </div>

              <VCardText :class="{ 'pb-1': isRetentionEnabled }" class="pa-0" sentry-unmask>
                <p>{{ t('settings.folders.updateFolder.description') }}</p>
              </VCardText>

              <VSelect
                sentry-unmask
                v-if="isRetentionEnabled"
                v-model="r$.$value.retention"
                :items="retentionDeletionList"
                :placeholder="t('settings.folders.form.timePeriod.label')"
                bg-color="false"
                data-test-id="settings-select-folder-parent"
                variant="outlined"
              />
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>

        <v-card-actions sentry-unmask>
          <OutlinedBtn data-test-id="settings-cancel-new-folder" @click="dialogOpener = false">
            {{ t('settings.folders.actions.cancel') }}
          </OutlinedBtn>
          <FilledBtn
            :disabled="createIsPending || updateIsPending"
            :loading="createIsPending || updateIsPending"
            data-test-id="settings-save-new-folder"
            @click.prevent="handleFolderSubmit"
          >
            {{ t('settings.folders.actions.save') }}
          </FilledBtn>
        </v-card-actions>
      </form>
    </v-card>
  </VDialog>
  <BeforeLeavingModal
    :dialog-popup="dialogPopupRef"
    @leave="dialogLeave"
    form="dialog"
  ></BeforeLeavingModal>
</template>

<style lang="scss" scoped>
@use '../../../assets/styles/settings' as settings;
@use 'sass:map';

.modal-edit {
  &__hidden {
    &:deep(.v-expansion-panel-title) {
      display: none;
    }
  }
}
</style>
